import { SimulationCtx } from '@/contexts/Simulation/SimulationCtx';
import { AppDispatch, RootState } from '@/store';
import {
  createItem,
  deleteItem,
  update,
} from '@/store/storeSlice';
import { TreeItem } from '@/store/types';
import {
  Button,
  ButtonIcon,
  DateTimePicker,
  Icon,
  InputCounter,
  Label,
  Select,
  SelectOption,
  Stack,
  Typography,
} from '@data-products-and-ai/react-components';
import { produce } from 'immer';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mapStatusToFullString, statusMapping } from '../../helpers/utils';

export type TFormResourceLifecycle = {
  RESOURCE_LIFECYCLE_ID: TreeItem<string>;
  RESOURCE_ID: TreeItem<string>;
  RESOURCE_LIFECYCLE_STATUS_ID: TreeItem<string>;
  RESOURCE_LIFECYCLE_STATUS: TreeItem<string>;
  RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP: TreeItem<number>;
  RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP: TreeItem<number>;
  RESOURCE_LIFECYCLE_STATUS_DISTANCE: TreeItem<number>;
  RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID: TreeItem<string>;
  SITE_ID: TreeItem<string>;
};
/**
 * FormResources Component
 * Manages the form for editing resource details within a simulation scenario
 * Handles form submission, input changes, time changes, and closing the form drawer
 *
 * @returns {JSX.Element} - The rendered component
 */
const FormResourceLifecycle = () => {
  const { simulationParams, setSimulationParams } = useContext(SimulationCtx);
  const dispatch: AppDispatch = useDispatch();
  const selectedScenario = useSelector((state: RootState) =>
    state.store.Simulation.scenarios.find((item) => item.is_selected),
  );
  const generateResourceLifecycleId = (): string => {
    const array = new Uint8Array(32); // 32 bytes = 64 hex characters
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
      '',
    );
  };

  const generatedNewId = generateResourceLifecycleId();
  const selectedItem =
    simulationParams.formItem && selectedScenario
      ? selectedScenario.data.resources.byId[simulationParams.formItem]
      : null;

  const InitialFormResourceLifecycle: TFormResourceLifecycle = {
    RESOURCE_LIFECYCLE_ID: {
      value: generatedNewId,
      originalValue: generatedNewId,
    },
    RESOURCE_ID: {
      value: selectedItem?.RESOURCE_ID.value ?? '',
      originalValue: selectedItem?.RESOURCE_ID.value ?? '',
    },
    RESOURCE_LIFECYCLE_STATUS_ID: {
      value: '',
      originalValue: '',
    },
    RESOURCE_LIFECYCLE_STATUS: {
      value: 'M',
      originalValue: 'M',
    },
    RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP: {
      value: 0,
      originalValue: 0,
    },
    RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP: {
      value: 0,
      originalValue: 0,
    },
    RESOURCE_LIFECYCLE_STATUS_DISTANCE: {
      value: 0,
      originalValue: 0,
    },
    RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID: {
      value: selectedItem?.RESOURCE_ID.value ?? '',
      originalValue: selectedItem?.RESOURCE_ID.value ?? '',
    },
    SITE_ID: {
      value: selectedItem?.SITE_ID.value ?? '',
      originalValue: selectedItem?.SITE_ID.value ?? '',
    },
  };

  const [form, setForm] = useState(InitialFormResourceLifecycle);
  const [selectedEditLifecycle, setSelectedEditLifecycle] = useState<
    string | null
  >(null);
  const [isAddingLifecycle, setIsAddingLifecycle] = useState(false);
  const [addedLifecycle, setAddedLifecycle] = useState<TFormResourceLifecycle>(
    InitialFormResourceLifecycle,
  );

  const resourceLifecycles =
    selectedScenario?.data.resource_lifecycle.allIds.filter((id) => {
      return (
        selectedScenario.data.resource_lifecycle.byId[id].RESOURCE_ID.value ===
        selectedItem?.RESOURCE_ID.value
      );
    });

  /**
   * handleTimeChange Function
   * Updates the form state when a time value changes
   * Sets the new time value for the specified form field
   *
   * @param {keyof TFormResources} id - The form field ID
   * @param {number} value - The new time value
   */
  const handleTimeChange = (
    id: keyof TFormResourceLifecycle,
    value: number,
  ) => {
    if (!isAddingLifecycle) {
      setForm((prevForm) => ({
        ...prevForm,
        [id]: {
          ...prevForm[id],
          value: value,
        },
      }));
    } else {
      setAddedLifecycle((prevForm) => ({
        ...prevForm,
        [id]: {
          ...prevForm[id],
          value: value,
        },
      }));
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { id, value } = event.target as HTMLInputElement & {
      id: string;
      value: string;
      type: string;
      checked: boolean;
    };
    const updateForm = (
      id: keyof TFormResourceLifecycle,
      newValue: unknown,
    ) => {
      let v = newValue;
      if (typeof form[id] === 'number') {
        v = parseInt(v as string);
      }
      if (!isAddingLifecycle) {
        setForm((prevForm) => ({
          ...prevForm,
          [id]: {
            ...prevForm[id],
            value: v,
          },
        }));
      } else {
        setAddedLifecycle((prevForm) => ({
          ...prevForm,
          [id]: {
            ...prevForm[id],
            value: v,
          },
        }));
      }
    };

    updateForm(id as keyof TFormResourceLifecycle, value);
  };

  /*  const handleInputChangeDebounced = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleInputChange(event, setForm, props.parameterIndex);
    },
    1000,
  ); */

  const handleCloseDrawer = () => {
    setSimulationParams(
      produce((draft) => {
        draft.formItem = undefined;
        draft.formArea = undefined;
        draft.drawerOpen = false;
      }),
    );

    setForm(InitialFormResourceLifecycle);
  };

  const canAddLifecycle =
    addedLifecycle.RESOURCE_LIFECYCLE_STATUS.value !== '' &&
    addedLifecycle.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value > 0 &&
    addedLifecycle.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value > 0;

  const resourceLifecycleList = resourceLifecycles?.map((id, idx) => {
    const lifecycle = selectedScenario?.data.resource_lifecycle.byId[id];
    if (!lifecycle) return null;

    const isLastItem = idx === resourceLifecycles.length - 1;
    const borderBottomStyle = isLastItem
      ? {}
      : { borderBottom: 'solid 1px #dadada' };

    if (
      !lifecycle.RESOURCE_LIFECYCLE_ID &&
      !lifecycle.RESOURCE_LIFECYCLE_STATUS &&
      !lifecycle.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP &&
      !lifecycle.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP
    )
      return;
    return (
      <div
        key={idx}
        style={{
          display: 'grid',
          gridTemplateColumns: '0.5fr 1fr 1fr 0.2fr 0.2fr',
          padding: 5,
          gap: 20,
          ...borderBottomStyle,
        }}
      >
        <div>
          {mapStatusToFullString(lifecycle.RESOURCE_LIFECYCLE_STATUS.value)}
        </div>
        <div>
          {new Date(
            parseInt(
              lifecycle.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value,
            ) * 1000,
          ).toLocaleString()}
        </div>
        <div>
          {new Date(
            parseInt(lifecycle.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value) *
              1000,
          ).toLocaleString()}
        </div>
        <div>
          <ButtonIcon
            icon="IconEdit"
            hoverType="color"
            onClick={() =>
              setSelectedEditLifecycle(lifecycle.RESOURCE_LIFECYCLE_ID.value)
            }
          />
        </div>
        <div>
          <ButtonIcon
            icon="IconTrash"
            hoverType="color"
            onClick={() =>
              handleDeleteLifecycle(lifecycle.RESOURCE_LIFECYCLE_ID.value)
            }
          />
        </div>
      </div>
    );
  });

  useEffect(() => {
    setAddedLifecycle(InitialFormResourceLifecycle);
  }, [isAddingLifecycle, selectedItem]);

  const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    dispatch(update({
      id: form.RESOURCE_LIFECYCLE_STATUS_ID.value,
      area: 'resource_lifecycle_status',
      content: {
        RESOURCE_LIFECYCLE_STATUS_ID: {
          value: form.RESOURCE_LIFECYCLE_STATUS_ID.value,
          originalValue: form.RESOURCE_LIFECYCLE_STATUS_ID.originalValue,
        },
        RESOURCE_LIFECYCLE_STATUS: {
          value: form.RESOURCE_LIFECYCLE_STATUS.value,
          originalValue: form.RESOURCE_LIFECYCLE_STATUS.originalValue,
        },
      },
    }));

    dispatch(
      update({
        id: form.RESOURCE_LIFECYCLE_ID.value,
        area: 'resource_lifecycle',
        content: form,
      }),
    );

    setSelectedEditLifecycle(null);

    handleCloseDrawer();
  };

  const handleAddSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const statusId = generateResourceLifecycleId();

    dispatch(
      createItem({
        key: 'RESOURCE_LIFECYCLE_STATUS_ID',
        area: 'resource_lifecycle_status',
        content: {
          RESOURCE_LIFECYCLE_STATUS_ID: {
            value: statusId,
            originalValue: statusId,
          },
          RESOURCE_LIFECYCLE_STATUS: {
            value: addedLifecycle.RESOURCE_LIFECYCLE_STATUS.value,
            originalValue: addedLifecycle.RESOURCE_LIFECYCLE_STATUS.value,
          },
          SITE_ID: {
            value: addedLifecycle.SITE_ID.value,
            originalValue: addedLifecycle.SITE_ID.originalValue,
          },
        },
      }),
    );

    dispatch(
      createItem({
        key: 'RESOURCE_LIFECYCLE_ID',
        area: 'resource_lifecycle',
        content: {
          ...addedLifecycle,
          RESOURCE_LIFECYCLE_STATUS_ID: {
            value: statusId,
            originalValue: statusId,
          },
        },
      }),
    );

    setIsAddingLifecycle(false);
  };

  const handleDeleteLifecycle = (lifecycleId: string) => {
    if (!selectedScenario) return;
    dispatch(
      deleteItem({
        id: lifecycleId,
        area: 'resource_lifecycle',
      }),
    );

    dispatch(
      deleteItem({
        id: selectedScenario.data.resource_lifecycle_status.byId[lifecycleId]
          .RESOURCE_LIFECYCLE_STATUS_ID.value,
        area: 'resource_lifecycle_status',
      }),
    );

    setSelectedEditLifecycle(null);
  };

  useEffect(() => {
    if (simulationParams.drawerOpen) return;
    handleCloseDrawer();
  }, [simulationParams.drawerOpen]);

  useEffect(() => {
    if (!selectedEditLifecycle) return;
    console.log('SelectedEditLifecycle', selectedEditLifecycle);
    const lifecycle =
      selectedScenario?.data.resource_lifecycle.byId[selectedEditLifecycle];
    if (!lifecycle) return;

    const formToChange: TFormResourceLifecycle = {
      RESOURCE_LIFECYCLE_ID: {
        value: lifecycle.RESOURCE_LIFECYCLE_ID.value,
        originalValue: lifecycle.RESOURCE_LIFECYCLE_ID.value,
      },
      RESOURCE_ID: {
        value: lifecycle.RESOURCE_ID.value,
        originalValue: lifecycle.RESOURCE_ID.value,
      },
      RESOURCE_LIFECYCLE_STATUS_ID: {
        value: lifecycle.RESOURCE_LIFECYCLE_STATUS_ID.value,
        originalValue: lifecycle.RESOURCE_LIFECYCLE_STATUS_ID.value,
      },
      RESOURCE_LIFECYCLE_STATUS: {
        value: lifecycle.RESOURCE_LIFECYCLE_STATUS.value,
        originalValue: lifecycle.RESOURCE_LIFECYCLE_STATUS.value,
      },
      RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP: {
        value: parseInt(
          lifecycle.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value,
        ),
        originalValue: parseInt(
          lifecycle.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value,
        ),
      },
      RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP: {
        value: parseInt(
          lifecycle.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value,
        ),
        originalValue: parseInt(
          lifecycle.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value,
        ),
      },
      RESOURCE_LIFECYCLE_STATUS_DISTANCE: {
        value: parseInt(lifecycle.RESOURCE_LIFECYCLE_STATUS_DISTANCE.value),
        originalValue: parseInt(
          lifecycle.RESOURCE_LIFECYCLE_STATUS_DISTANCE.value,
        ),
      },
      RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID: {
        value: lifecycle.RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID.value,
        originalValue:
          lifecycle.RESOURCE_LIFECYCLE_STATUS_MOVED_RESOURCE_ID.originalValue ??
          '',
      },
      SITE_ID: {
        value: lifecycle.SITE_ID.value,
        originalValue:
          lifecycle.SITE_ID.originalValue ?? selectedItem?.SITE_ID.value ?? '',
      },
    };
    setForm(formToChange);
  }, [selectedEditLifecycle]);

  if (!selectedItem || !selectedScenario) return;

  /*   const MTTR_Illness = formatHoursToTime(form.RESOURCE_MTTR_ILLNESS.value);
  const MTTR_Tech = formatHoursToTime(form.RESOURCE_MTTR_TECH.value);
  const MTTR_Vacation = formatHoursToTime(form.RESOURCE_MTTR_VACATION.value); */

  const formMargin = 30;

  return (
    <>
      {!selectedEditLifecycle && !isAddingLifecycle ? (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: formMargin,
            }}
          >
            <Typography tag="h4" color="#8a00e5">
              Resource Lifecycles
            </Typography>
            {!isAddingLifecycle && (
              <Button
                type="primaryNoShadow"
                icon={<Icon icon="IconAdd" color="#ffff" iconSize={0.9} />}
                allCaps={false}
                onClick={() => setIsAddingLifecycle(true)}
              >
                Add
              </Button>
            )}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 20,
              marginBottom: formMargin,
              marginTop: formMargin / 2,
            }}
          >
            <div>
              <Label tag="textsmall_strong">Site</Label>
              <Typography tag="textsmall">
                {
                  selectedScenario.data.sites.byId[selectedItem.SITE_ID.value]
                    .SITE.value
                }
              </Typography>
            </div>
            <div>
              <Label tag="textsmall_strong">Resource</Label>
              <Typography tag="textsmall">
                {selectedItem.RESOURCE_NAME.value}
              </Typography>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflowY: 'auto',
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '0.5fr 1fr 1fr 0.2fr 0.2fr',
                  padding: 5,
                  gap: 20,

                  borderBottom: 'solid 1px #dadada',
                  fontSize: 11,
                  fontWeight: 'bold',
                }}
              >
                <div>Status</div>
                <div>Start</div>
                <div>End</div>
              </div>
              {resourceLifecycleList}
            </div>
          </div>
        </>
      ) : selectedEditLifecycle ? (
        <form onSubmit={handleEditSubmit}>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                padding: 5,
                gap: 20,
                marginBottom: 30,

                borderBottom: 'solid 1px #dadada',
                fontSize: 11,
                fontWeight: 'bold',
              }}
            >
              <Typography tag="h4" color="#8a00e5">
                Edit Resource Lifecycle
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <Label tag="textsmall_strong">Resource Lifecycle</Label>
                  <Typography tag="textdefault">
                    {selectedEditLifecycle}
                  </Typography>
                </div>
              </div>
              <div>
                <Label tag="textsmall_strong">Resource</Label>
                <Typography tag="textdefault">
                  {selectedItem.RESOURCE_NAME.value}
                </Typography>
              </div>
            </div>
            <div>
              <Label marginTop={formMargin} tag="textsmall_strong">
                Status
              </Label>

              <Select
                onChange={(event) => handleInputChange(event)}
                defaultValue={form.RESOURCE_LIFECYCLE_STATUS.value}
                id="RESOURCE_LIFECYCLE_STATUS"
              >
                {Object.keys(statusMapping).map((status) => {
                  return (
                    <SelectOption
                      key={status}
                      label={mapStatusToFullString(status)}
                      value={status}
                    />
                  );
                })}
              </Select>
            </div>
            <div>
              <Label marginTop={formMargin} tag="textsmall_strong">
                Distance
              </Label>
              <InputCounter
                id="RESOURCE_LIFECYCLE_STATUS_DISTANCE"
                defaultValue={form.RESOURCE_LIFECYCLE_STATUS_DISTANCE.value}
                min={0}
                onChange={(value) =>
                  handleInputChange({
                    target: {
                      id: 'RESOURCE_LIFECYCLE_STATUS_DISTANCE',
                      value: value.toString(),
                      type: 'number',
                      checked: false,
                    },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
              />
            </div>
            <div>
              <Label marginTop={formMargin} tag="textsmall_strong">
                Start Date
              </Label>
              {form.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value}
              <DateTimePicker
                id="RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP"
                timeFormat="HH:mm:ss"
                dateFormat="YYYY/MM/DD"
                defaultValue={
                  form.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value
                }
                onChange={(value) =>
                  handleTimeChange(
                    'RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP',
                    value || new Date().getTime() / 1000,
                  )
                }
              />
            </div>
            <div>
              <Label marginTop={formMargin} tag="textsmall_strong">
                End Date
              </Label>

              <DateTimePicker
                id="RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP"
                timeFormat="HH:mm:ss"
                dateFormat="YYYY/MM/DD"
                defaultValue={
                  form.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value
                }
                onChange={(value) =>
                  handleTimeChange(
                    'RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP',
                    value || new Date().getTime() / 1000,
                  )
                }
              />
            </div>
          </div>

          <div
            style={{
              position: 'fixed',
              bottom: 0,
              marginTop: 30,
              backgroundColor: 'white',
              paddingBottom: 20,
              paddingTop: 20,
              width: `calc(100% - 40px)`,
              borderTop: 'solid 1px #ededed',
            }}
          >
            <Stack direction="row" distribute="space-around">
              <Button
                buttonType="button"
                type="primaryOutline"
                width="200px"
                onClick={handleCloseDrawer}
              >
                Cancel
              </Button>
              <Button buttonType="submit" type="primaryNoShadow" width="200px">
                Update
              </Button>
            </Stack>
          </div>
        </form>
      ) : (
        isAddingLifecycle && (
          <form
            style={{ height: '100%' }}
            onSubmit={(e) => {
              handleAddSubmit(e);
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflowY: 'auto',
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 5,
                    gap: 20,
                    marginBottom: 30,

                    borderBottom: 'solid 1px #dadada',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                >
                  <Typography tag="h4" color="#8a00e5">
                    Add Resource Lifecycle
                  </Typography>
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <div>
                      <Label tag="textsmall_strong">Resource</Label>
                      <Typography tag="textdefault">
                        {selectedItem.RESOURCE_NAME.value}
                      </Typography>
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 20,
                  }}
                >
                  <div>
                    <Label marginTop={formMargin} tag="textsmall_strong">
                      Status
                    </Label>

                    <Select
                      onChange={(event) => handleInputChange(event)}
                      defaultValue={form.RESOURCE_LIFECYCLE_STATUS_ID.value}
                      id="RESOURCE_LIFECYCLE_STATUS"
                    >
                      {Object.keys(statusMapping).map((status) => {
                        return (
                          <SelectOption
                            key={status}
                            label={mapStatusToFullString(status)}
                            value={status}
                          />
                        );
                      })}
                    </Select>
                  </div>
                  <div>
                    <Label marginTop={formMargin} tag="textsmall_strong">
                      Distance
                    </Label>
                    <InputCounter
                      id="RESOURCE_LIFECYCLE_STATUS_DISTANCE"
                      defaultValue={
                        form.RESOURCE_LIFECYCLE_STATUS_DISTANCE.value
                      }
                      min={0}
                      onChange={(value) =>
                        handleInputChange({
                          target: {
                            id: 'RESOURCE_LIFECYCLE_STATUS_DISTANCE',
                            value: value.toString(),
                            type: 'number',
                            checked: false,
                          },
                        } as React.ChangeEvent<HTMLInputElement>)
                      }
                    />
                  </div>
                  <div>
                    <Label marginTop={formMargin} tag="textsmall_strong">
                      Start Date
                    </Label>

                    <DateTimePicker
                      id="RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP"
                      timeFormat="HH:mm:ss"
                      dateFormat="YYYY/MM/DD"
                      defaultValue={Math.floor(
                        new Date(
                          form.RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP.value,
                        ).getTime() / 1000,
                      )}
                      onChange={(value) =>
                        handleTimeChange(
                          'RESOURCE_LIFECYCLE_STATUS_START_TIMESTAMP',
                          value || new Date().getTime() / 1000,
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label marginTop={formMargin} tag="textsmall_strong">
                      End Date
                    </Label>

                    <DateTimePicker
                      id="RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP"
                      timeFormat="HH:mm:ss"
                      dateFormat="YYYY/MM/DD"
                      defaultValue={Math.floor(
                        new Date(
                          form.RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP.value,
                        ).getTime() / 1000,
                      )}
                      onChange={(value) =>
                        handleTimeChange(
                          'RESOURCE_LIFECYCLE_STATUS_END_TIMESTAMP',
                          value || new Date().getTime() / 1000,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div
                style={{
                  paddingTop: 10,
                  paddingBottom: 10,
                }}
              ></div>
              <div
                style={{
                  position: 'sticky',
                  bottom: 0,
                  marginTop: 30,
                  backgroundColor: 'white',
                  paddingBottom: 5,
                  paddingTop: 20,
                  borderTop: 'solid 1px #ededed',
                }}
              >
                <Stack direction="row" distribute="space-around">
                  <Button
                    buttonType="button"
                    type="primaryOutline"
                    width="200px"
                    onClick={() => setIsAddingLifecycle(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    buttonType="submit"
                    type="primaryNoShadow"
                    width="200px"
                    disabled={!canAddLifecycle}
                  >
                    Add
                  </Button>
                </Stack>
              </div>
            </div>
          </form>
        )
      )}
    </>
  );
};

export default FormResourceLifecycle;
